'use client'

import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import type { Store } from 'maps/types'
import { getMarkers } from 'maps/utils/mapOptions/getMarkers'
import { useEffect, useRef, useState } from 'react'
import { type Root, createRoot } from 'react-dom/client'

interface MarkerProps {
	readonly map: google.maps.Map
	readonly store: Store
}

export function Marker({ map, store }: MarkerProps): React.ReactNode {
	const [isReady, setIsReady] = useState<boolean>(false)
	const { type } = useMapState()
	const { id: storeId, addresses } = store
	const {
		coordinates: { lat, lng },
		address,
		city,
		postalCode,
	} = addresses

	const { expanded, setExpanded } = useExpandedStore()

	const { SelectedMarker, UnselectedMarker } = getMarkers(type)

	const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>(null)
	const rootRef = useRef<Root | null>(null)

	const isExpanded = expanded?.id === storeId

	const handleOnClick = () => {
		setExpanded(store)

		const targetStoreElement = document.getElementById(`store-${storeId}`)
		if (targetStoreElement) {
			targetStoreElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			})
		}
	}

	const initMarker = async () => {
		const title = `${address}, ${city}, ${postalCode}`
		const { AdvancedMarkerElement } = (await google.maps.importLibrary(
			'marker'
		)) as google.maps.MarkerLibrary
		const container = document.createElement('div')
		rootRef.current = createRoot(container)
		// Creates a new Marker associated with the map
		markerRef.current = new AdvancedMarkerElement({
			map,
			title,
			position: { lat, lng },
			zIndex: UnselectedMarker.zIndex,
			content: container,
		})
		markerRef.current.addListener('click', handleOnClick)
		setIsReady(true)
	}

	const updateMarker = () => {
		if (rootRef.current && markerRef.current) {
			const UpdatedMarker = isExpanded ? SelectedMarker : UnselectedMarker
			rootRef.current.render(UpdatedMarker.component)
			markerRef.current.zIndex = UpdatedMarker.zIndex
		}
	}

	useEffect(() => {
		return () => {
			if (markerRef.current) {
				markerRef.current.map = null
			}
		}
	}, [])

	useEffect(() => {
		if (isReady) {
			updateMarker()
		}
	}, [isExpanded, isReady])

	useEffect(() => {
		if (!rootRef.current) {
			initMarker()
		}
	}, [expanded])

	return null
}
